import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";
import { ScreenTips } from "@/components/GuidedHighlights";
import { COACHMARKS } from "@/lib/coachmarks";
import { StripeConnectCard, ResellerPlansCard, SeatsPlansExplainer } from "./StripeAndPlansCards";
import {
  Palette, Link2, Ticket, Loader2, Copy, Plus, Check, Handshake, Sparkles, Trash2, Lock, ArrowRight,
  Users, Search, Power, PowerOff, ChevronDown, ChevronUp,
} from "lucide-react";

const inputClass = "w-full rounded-2xl border-2 border-[#E5DECF] bg-white px-4 py-3 font-body text-[#1F2937] outline-none focus:border-[#0D9488]";
const COLORS = ["#0D9488", "#E05A47", "#0284C7", "#10B981", "#7A3FF2", "#D97706", "#DB2777", "#0F766E"];

export default function ResellerHome() {
  const [brand, setBrand] = useState(null);
  const [stats, setStats] = useState({ codes_total: 0, codes_redeemed: 0, seats_total: 0, seats_available: 0 });
  const [subscription, setSubscription] = useState(null);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [logo, setLogo] = useState("");
  const [color, setColor] = useState("#0D9488");
  const [saving, setSaving] = useState(false);
  const [genCount, setGenCount] = useState(10);
  const [genning, setGenning] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientsOpen, setClientsOpen] = useState(true);
  const [clientQuery, setClientQuery] = useState("");
  const [toggling, setToggling] = useState({});
  const [selected, setSelected] = useState({});   // code -> bool
  const [bulking, setBulking] = useState(false);
  const [stripeConnected, setStripeConnected] = useState(false);

  const load = async () => {
    const b = await api.getResellerBrand();
    if (b.brand) { setBrand(b.brand); setName(b.brand.name); setSlug(b.brand.slug); setLogo(b.brand.logo_url || ""); setColor(b.brand.accent_color || "#0D9488"); }
    setStats(b.stats);
    setSubscription(b.subscription);
    const c = await api.getResellerCodes();
    setCodes(c.codes);
    try {
      const cl = await api.getResellerClients();
      setClients(cl.clients || []);
    } catch { setClients([]); }
    try {
      const s = await api.resellerStripeStatus();
      setStripeConnected(!!s.connected);
    } catch { setStripeConnected(false); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const active = subscription?.status === "active";

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.saveResellerBrand({ name: name.trim(), slug: slug.trim() || undefined, logo_url: logo.trim() || null, accent_color: color });
      setBrand(r.brand); setSlug(r.brand.slug);
      toast.success("Brand saved!");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const generate = async () => {
    if (!brand) { toast.error("Save your brand first."); return; }
    setGenning(true);
    try { await api.genResellerCodes(Number(genCount) || 10); await load(); toast.success(`${genCount} code(s) generated`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setGenning(false); }
  };

  const revoke = async (code) => {
    try { await api.revokeResellerCode(code); await load(); toast.success("Seat reclaimed"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleCode = async (code) => {
    setToggling((t) => ({ ...t, [code]: true }));
    try {
      const r = await api.toggleResellerCode(code);
      toast.success(r.status === "active" ? "License enabled — client has access again." : "License disabled — client no longer has premium access.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setToggling((t) => ({ ...t, [code]: false })); }
  };

  const filteredClients = clients.filter((c) => {
    if (!clientQuery.trim()) return true;
    const q = clientQuery.trim().toLowerCase();
    return (
      c.code.toLowerCase().includes(q) ||
      (c.client?.name || "").toLowerCase().includes(q) ||
      (c.client?.email || "").toLowerCase().includes(q)
    );
  });

  const selectedCodes = filteredClients.filter((c) => selected[c.code]).map((c) => c.code);
  const allFilteredChecked = filteredClients.length > 0 && filteredClients.every((c) => selected[c.code]);
  const someFilteredChecked = filteredClients.some((c) => selected[c.code]);

  const toggleSelect = (code) => setSelected((s) => ({ ...s, [code]: !s[code] }));
  const clearSelection = () => setSelected({});
  const selectAllFiltered = () => {
    if (allFilteredChecked) { clearSelection(); return; }
    const next = { ...selected };
    for (const c of filteredClients) next[c.code] = true;
    setSelected(next);
  };
  const bulkApply = async (status) => {
    if (selectedCodes.length === 0) return;
    const verb = status === "disabled" ? "Disable" : "Enable";
    if (!window.confirm(`${verb} ${selectedCodes.length} license${selectedCodes.length === 1 ? "" : "s"}? Clients will ${status === "disabled" ? "immediately lose premium access" : "have their premium access restored"}.`)) return;
    setBulking(true);
    try {
      const r = await api.bulkToggleResellerCodes(selectedCodes, status);
      toast.success(`${r.updated_count} license${r.updated_count === 1 ? "" : "s"} ${status === "disabled" ? "disabled" : "enabled"}${r.skipped_count ? ` · ${r.skipped_count} skipped` : ""}`);
      clearSelection();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulking(false); }
  };

  const brandedUrl = brand ? `${window.location.origin}/r/${brand.slug}` : "";
  const copy = (t) => copyToClipboard(t, "Copied!");

  if (loading) return <div className="p-16 text-center"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>;

  const usedPct = stats.seats_total ? Math.min(100, Math.round((stats.codes_total / stats.seats_total) * 100)) : 0;
  const atCap = stats.seats_available <= 0;

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#F59E0B]"><Handshake size={14} /> Partner Dashboard</div>
      <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-[#1F2937]">Your White-Label</h1>

      {/* Subscription gate */}
      {!active && (
        <div className="mt-6 rounded-3xl border-2 border-[#FF9E1B] bg-[#FF9E1B]/10 p-6" data-testid="reseller-upgrade-gate">
          <div className="flex items-center gap-2 text-[#B45309]"><Lock size={18} /><h2 className="font-heading text-lg font-extrabold">Activate your partner plan</h2></div>
          <p className="mt-2 font-body text-sm text-[#6B7280]">Purchase the Reseller Partner plan to unlock license seats you can hand out under your own brand. You can set up your brand below anytime.</p>
          <Link to="/pricing" data-testid="reseller-choose-plan" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#FF9E1B] px-6 py-3 font-heading font-bold text-[#1F2937] hover:brightness-95">
            Choose a plan <ArrowRight size={16} />
          </Link>
        </div>
      )}

      {/* Seat meter */}
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <Stat label="Seats used" value={`${stats.codes_total} / ${stats.seats_total}`} icon={Ticket} />
        <Stat label="Redeemed" value={stats.codes_redeemed} icon={Check} />
        <Stat label="Seats available" value={stats.seats_available} icon={Plus} />
      </div>
      {stats.seats_total > 0 && (
        <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-[#E5DECF]" data-testid="seat-meter">
          <div className="h-full rounded-full bg-[#0D9488] transition-all" style={{ width: `${usedPct}%` }} />
        </div>
      )}

      {/* Brand editor */}
      <div className="mt-8 rounded-3xl border border-[#E5DECF] bg-white p-6">
        <div className="flex items-center gap-2"><Palette size={18} className="text-[#0D9488]" /><h2 className="font-heading text-lg font-bold text-[#1F2937]">Brand</h2></div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-bold text-[#1F2937] mb-1">Brand name</label>
            <input data-testid="rb-name" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} placeholder="Acme Learning" />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#1F2937] mb-1">Link name (slug)</label>
            <input data-testid="rb-slug" value={slug} onChange={(e) => setSlug(e.target.value)} className={inputClass} placeholder="acme-learning" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-bold text-[#1F2937] mb-1">Logo URL <span className="font-normal text-[#94A3B8]">(optional)</span></label>
            <input data-testid="rb-logo" value={logo} onChange={(e) => setLogo(e.target.value)} className={inputClass} placeholder="https://…/logo.png" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-bold text-[#1F2937] mb-2">Accent color</label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setColor(c)} data-testid={`rb-color-${c}`}
                  className={`h-9 w-9 rounded-full border-2 transition-transform ${color === c ? "scale-110 border-[#1F2937]" : "border-transparent"}`} style={{ background: c }} />
              ))}
            </div>
          </div>
        </div>

        {/* Live preview */}
        <div className="mt-5 rounded-2xl border border-[#E5DECF] p-4" style={{ background: `${color}0D` }}>
          <div className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">Preview</div>
          <div className="mt-2 flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-2xl text-white" style={{ background: color }}>
              {logo ? <img src={logo} alt="" className="h-full w-full object-cover" /> : <Sparkles size={20} />}
            </div>
            <div className="font-heading text-lg font-extrabold text-[#1F2937]">{name || "Your Brand"}</div>
          </div>
        </div>

        <button onClick={save} disabled={saving} data-testid="rb-save" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#0D9488] px-6 py-2.5 font-heading font-bold text-white hover:bg-[#0B7F74] disabled:opacity-50">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Save brand
        </button>
      </div>

      {/* Branded link */}
      {brand && (
        <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6">
          <div className="flex items-center gap-2"><Link2 size={18} className="text-[#0D9488]" /><h2 className="font-heading text-lg font-bold text-[#1F2937]">Your branded link</h2></div>
          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 truncate rounded-2xl bg-[#F2ECE1] px-4 py-3 font-body text-sm text-[#1F2937]" data-testid="rb-link">{brandedUrl}</code>
            <button onClick={() => copy(brandedUrl)} className="rounded-2xl border-2 border-[#E5DECF] p-3 hover:border-[#0D9488]"><Copy size={16} /></button>
          </div>
        </div>
      )}

      {/* Codes */}
      <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2"><Ticket size={18} className="text-[#0D9488]" /><h2 className="font-heading text-lg font-bold text-[#1F2937]">License codes</h2></div>
          <div className="flex items-center gap-2">
            <input type="number" min="1" max="100" value={genCount} onChange={(e) => setGenCount(e.target.value)} className="w-20 rounded-xl border-2 border-[#E5DECF] px-3 py-2 font-body disabled:opacity-50" data-testid="rb-gen-count" disabled={!active || atCap} />
            <button onClick={generate} disabled={genning || !active || atCap} data-testid="rb-generate" className="inline-flex items-center gap-2 rounded-full bg-[#10B981] px-4 py-2 font-heading font-bold text-white hover:bg-[#059669] disabled:opacity-50">
              {genning ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Generate
            </button>
          </div>
        </div>
        {active && atCap && <p className="mt-3 rounded-xl bg-[#FF9E1B]/10 px-3 py-2 font-body text-sm font-semibold text-[#B45309]">All seats issued. Reclaim an unused code or upgrade your plan to add more.</p>}
        {!active && <p className="mt-3 font-body text-sm text-[#94A3B8]">Activate a partner plan to start issuing seats.</p>}
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {codes.length === 0 && active && <p className="font-body text-sm text-[#94A3B8]">No codes yet — generate some to hand out.</p>}
          {codes.map((c) => (
            <div key={c.code} className={`flex items-center justify-between rounded-2xl border px-4 py-2.5 ${c.redeemed_by ? "border-[#E5DECF] bg-[#F2ECE1]/50 opacity-70" : "border-[#E5DECF] bg-white"}`} data-testid={`rb-code-${c.code}`}>
              <code className="font-body text-sm font-bold text-[#1F2937]">{c.code}</code>
              {c.redeemed_by ? (
                <span className="text-xs font-bold text-[#94A3B8]">Redeemed</span>
              ) : (
                <div className="flex items-center gap-2">
                  <button onClick={() => copy(c.code)} className="text-[#0D9488] hover:opacity-70" aria-label="Copy code"><Copy size={15} /></button>
                  <button onClick={() => revoke(c.code)} data-testid={`rb-revoke-${c.code}`} className="text-[#E05A47] hover:opacity-70" aria-label="Reclaim seat"><Trash2 size={15} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Clients — who redeemed which code, with enable/disable per code */}
      <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6" data-testid="reseller-clients-card">
        <button
          type="button"
          onClick={() => setClientsOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-3"
          data-testid="reseller-clients-toggle"
        >
          <div className="flex items-center gap-2">
            <Users size={18} className="text-[#0D9488]" />
            <h2 className="font-heading text-lg font-bold text-[#1F2937]">
              Clients <span className="font-body text-sm font-normal text-[#94A3B8]">({clients.length})</span>
            </h2>
          </div>
          {clientsOpen ? <ChevronUp size={18} className="text-[#6B7280]" /> : <ChevronDown size={18} className="text-[#6B7280]" />}
        </button>
        {clientsOpen && (
          <>
            <p className="mt-1 font-body text-sm text-[#6B7280]">Everyone who redeemed one of your license codes. Disable a code to instantly revoke premium access for that client (their account stays, they can be re-enabled anytime).</p>
            {clients.length > 0 && (
              <div className="mt-4 flex items-center gap-2 rounded-2xl border-2 border-[#E5DECF] bg-[#FAF7F2] px-3 py-2">
                <Search size={16} className="text-[#94A3B8]" />
                <input
                  data-testid="reseller-clients-search"
                  value={clientQuery}
                  onChange={(e) => setClientQuery(e.target.value)}
                  placeholder="Search by name, email, or code…"
                  className="w-full bg-transparent outline-none font-body text-sm text-[#1F2937]"
                />
                {clientQuery && (
                  <button onClick={() => setClientQuery("")} className="text-xs font-bold text-[#0D9488]" data-testid="reseller-clients-search-clear">Clear</button>
                )}
              </div>
            )}
            {selectedCodes.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-[#0D9488] bg-[#0D9488]/5 px-4 py-3" data-testid="reseller-bulk-bar">
                <div className="font-body text-sm font-bold text-[#0B7F74]" data-testid="reseller-bulk-count">
                  {selectedCodes.length} selected
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => bulkApply("disabled")} disabled={bulking} data-testid="reseller-bulk-disable" className="inline-flex items-center gap-1.5 rounded-full bg-[#E05A47] px-4 py-1.5 text-sm font-heading font-bold text-white hover:brightness-95 disabled:opacity-50">
                    {bulking ? <Loader2 size={14} className="animate-spin" /> : <PowerOff size={14} />} Disable
                  </button>
                  <button onClick={() => bulkApply("active")} disabled={bulking} data-testid="reseller-bulk-enable" className="inline-flex items-center gap-1.5 rounded-full bg-[#10B981] px-4 py-1.5 text-sm font-heading font-bold text-white hover:brightness-95 disabled:opacity-50">
                    {bulking ? <Loader2 size={14} className="animate-spin" /> : <Power size={14} />} Enable
                  </button>
                  <button onClick={clearSelection} data-testid="reseller-bulk-clear" className="rounded-full border-2 border-[#E5DECF] px-3 py-1.5 text-xs font-bold text-[#6B7280] hover:border-[#0D9488] hover:text-[#0D9488]">Clear</button>
                </div>
              </div>
            )}
            {clients.length === 0 ? (
              <div className="mt-6 rounded-2xl border border-dashed border-[#E5DECF] bg-[#FAF7F2] p-6 text-center" data-testid="reseller-clients-empty">
                <Users size={28} className="mx-auto text-[#94A3B8]" />
                <p className="mt-2 font-body text-sm text-[#6B7280]">No one has redeemed a code yet. Share your branded link or license codes to onboard families and teachers under your brand.</p>
              </div>
            ) : filteredClients.length === 0 ? (
              <p className="mt-4 font-body text-sm text-[#94A3B8]" data-testid="reseller-clients-no-match">No clients match "{clientQuery}".</p>
            ) : (
              <div className="mt-4 space-y-2" data-testid="reseller-clients-list">
                {filteredClients.length > 1 && (
                  <label className="flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs font-bold text-[#6B7280]">
                    <input
                      type="checkbox"
                      data-testid="reseller-clients-select-all"
                      checked={allFilteredChecked}
                      ref={(el) => { if (el) el.indeterminate = !allFilteredChecked && someFilteredChecked; }}
                      onChange={selectAllFiltered}
                      className="h-4 w-4 rounded border-[#94A3B8] text-[#0D9488] focus:ring-[#0D9488]"
                    />
                    Select all ({filteredClients.length})
                  </label>
                )}
                {filteredClients.map((row) => {
                  const isActive = row.status === "active";
                  const isDisabled = row.status === "disabled";
                  const busy = !!toggling[row.code];
                  const isSelected = !!selected[row.code];
                  return (
                    <div
                      key={row.code}
                      className={`grid grid-cols-1 gap-3 rounded-2xl border p-4 sm:grid-cols-[auto_1.5fr_1fr_auto] sm:items-center ${isDisabled ? "border-[#E05A47]/30 bg-[#E05A47]/5" : "border-[#E5DECF] bg-white"} ${isSelected ? "ring-2 ring-[#0D9488]" : ""}`}
                      data-testid={`client-row-${row.code}`}
                    >
                      <label className="flex items-center pl-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(row.code)}
                          data-testid={`select-client-${row.code}`}
                          className="h-4 w-4 rounded border-[#94A3B8] text-[#0D9488] focus:ring-[#0D9488]"
                          aria-label={`Select ${row.client?.name || row.code}`}
                        />
                      </label>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-heading text-sm font-extrabold text-[#1F2937]" data-testid={`client-name-${row.code}`}>
                            {row.client?.name || "Unknown"}
                          </div>
                          <span className="rounded-full bg-[#F2ECE1] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#6B7280]">
                            {row.client?.role || "—"}
                          </span>
                          {isDisabled && (
                            <span data-testid={`client-status-disabled-${row.code}`} className="rounded-full bg-[#E05A47]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#B4402F]">Disabled</span>
                          )}
                          {isActive && (
                            <span data-testid={`client-status-active-${row.code}`} className="rounded-full bg-[#10B981]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#047857]">Active</span>
                          )}
                        </div>
                        <div className="mt-0.5 font-body text-xs text-[#6B7280]">
                          {row.client?.email || "no email"} · <span className="font-bold text-[#1F2937]">{row.students_count}</span> student{row.students_count === 1 ? "" : "s"}
                        </div>
                      </div>
                      <div className="font-body text-xs text-[#6B7280]">
                        <div className="flex items-center gap-2">
                          <code className="rounded-lg bg-[#F2ECE1] px-2 py-1 text-[11px] font-bold text-[#1F2937]" data-testid={`client-code-${row.code}`}>{row.code}</code>
                          <button onClick={() => copy(row.code)} className="text-[#0D9488] hover:opacity-70" aria-label="Copy code"><Copy size={13} /></button>
                        </div>
                        <div className="mt-1">Redeemed {row.redeemed_at ? new Date(row.redeemed_at).toLocaleDateString() : "—"}</div>
                      </div>
                      <button
                        onClick={() => toggleCode(row.code)}
                        disabled={busy}
                        data-testid={`toggle-code-${row.code}`}
                        aria-pressed={isActive}
                        className={`inline-flex items-center gap-2 justify-center rounded-full px-4 py-2 text-sm font-heading font-bold transition-all disabled:opacity-50 ${
                          isActive
                            ? "bg-white border-2 border-[#E05A47] text-[#E05A47] hover:bg-[#E05A47] hover:text-white"
                            : "bg-[#0D9488] text-white hover:bg-[#0B7F74]"
                        }`}
                      >
                        {busy ? <Loader2 size={14} className="animate-spin" /> : isActive ? <PowerOff size={14} /> : <Power size={14} />}
                        {isActive ? "Disable" : "Enable"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <StripeConnectCard />
      <SeatsPlansExplainer />
      <ResellerPlansCard stripeConnected={stripeConnected} />

      <ScreenTips screenKey="reseller-home" steps={COACHMARKS["reseller-home"]} />
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-3xl border border-[#E5DECF] bg-white p-5">
      <div className="flex items-center gap-2 text-[#0D9488]"><Icon size={18} /><span className="text-xs uppercase tracking-wide font-semibold text-[#6B7280]">{label}</span></div>
      <div className="mt-1 font-heading text-3xl font-extrabold text-[#1F2937]">{value}</div>
    </div>
  );
}
